// components/team/GalleryRequestsQueue.jsx
// Version COMPLÈTE avec workflow: Pending → Approved → Galerie créée

import React, { useEffect, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext";
import { CheckCircle, XCircle, Users, TrendingUp, ExternalLink, Plus } from "lucide-react";
import AddGalleryForm from "./AddGalleryForm";

const GalleryRequestsQueue = () => {
  const { user } = useAuth();
  const [pendingRequests, setPendingRequests] = useState([]);
  const [approvedRequests, setApprovedRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showAddGalleryModal, setShowAddGalleryModal] = useState(null);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      setLoading(true);
      
      // Charger les pending (pour approuver/rejeter)
      const { data: pending } = await supabase
        .from("v_top_gallery_requests")
        .select("*")
        .limit(50);

      setPendingRequests(pending || []);

      // Charger les approved (pour ajouter la galerie)
      const { data: approved } = await supabase
        .from("GalerieDemandes")
        .select("*")
        .eq("status", "approved")
        .is("integrated_gallery_id", null) // Pas encore intégrées
        .order("reviewed_at", { ascending: false });

      // Grouper par galerie
      const approvedGrouped = [];
      const seen = new Set();
      
      approved?.forEach(req => {
        const key = req.gallery_name;
        if (!seen.has(key)) {
          seen.add(key);
          approvedGrouped.push({
            gallery_name: req.gallery_name,
            gallery_city: req.gallery_city,
            gallery_country: req.gallery_country,
            gallery_website: req.gallery_website,
            request_ids: [req.id],
            reviewed_at: req.reviewed_at
          });
        } else {
          const existing = approvedGrouped.find(g => g.gallery_name === key);
          if (existing) {
            existing.request_ids.push(req.id);
          }
        }
      });

      setApprovedRequests(approvedGrouped);
    } catch (e) {
      console.error("❌ Erreur chargement demandes:", e);
    } finally {
      setLoading(false);
    }
  };

  const approveRequest = async (galleryName) => {
    try {
      const { error } = await supabase
        .from("GalerieDemandes")
        .update({ 
          status: "approved",
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString()
        })
        .eq("gallery_name", galleryName)
        .eq("status", "pending");

      if (error) throw error;

      alert(`✅ Galerie "${galleryName}" approuvée !`);
      loadRequests();
    } catch (e) {
      console.error("❌ Erreur approbation:", e);
      alert("Erreur lors de l'approbation");
    }
  };

  const rejectRequest = async () => {
    if (!showRejectModal || !rejectReason.trim()) {
      alert("Merci de préciser une raison");
      return;
    }

    try {
      const { error } = await supabase
        .from("GalerieDemandes")
        .update({
          status: "rejected",
          rejection_reason: rejectReason,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString()
        })
        .eq("gallery_name", showRejectModal)
        .eq("status", "pending");

      if (error) throw error;

      alert(`✅ Demande rejetée`);
      setShowRejectModal(null);
      setRejectReason("");
      loadRequests();
    } catch (e) {
      console.error("❌ Erreur rejet:", e);
      alert("Erreur lors du rejet");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Bouton ajout direct */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-light text-gray-900">Galeries</h2>
        <button
          onClick={() => setShowAddGalleryModal({ 
            gallery_name: "",
            gallery_city: "",
            gallery_country: "",
            gallery_website: "",
            request_ids: null // Pas de demande liée
          })}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Ajouter une galerie
        </button>
      </div>

      {/* Section 1: Demandes à approuver */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-light text-gray-900">
            À approuver ({pendingRequests.length})
          </h3>
        </div>

        {/* Stats rapides */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-2xl font-light text-blue-900">{pendingRequests.length}</div>
            <div className="text-sm text-blue-700">En attente</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-2xl font-light text-red-900">
              {pendingRequests.filter(r => r.request_count > 1).length}
            </div>
            <div className="text-sm text-red-700">Prioritaires</div>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="text-2xl font-light text-gray-900">
              {pendingRequests.reduce((sum, r) => sum + r.request_count, 0)}
            </div>
            <div className="text-sm text-gray-700">Demandes totales</div>
          </div>
        </div>

        {/* Liste pending */}
        {pendingRequests.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <p className="text-gray-500">Aucune demande en attente</p>
          </div>
        ) : (
          <div className="space-y-4">
            {pendingRequests.map((req) => (
              <div 
                key={req.gallery_name}
                className="bg-white border border-gray-200 rounded-lg p-6 hover:border-gray-400 transition-colors"
              >
                <div className="flex items-start justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-medium text-gray-900">
                        {req.gallery_name}
                      </h4>
                      
                      {req.request_count > 1 && (
                        <span className="flex items-center gap-1 px-2 py-1 text-xs bg-red-100 text-red-800 rounded font-medium">
                          <TrendingUp className="w-3 h-3" />
                          Prioritaire
                        </span>
                      )}
                      
                      <span className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                        <Users className="w-3 h-3" />
                        {req.request_count} client{req.request_count > 1 ? 's' : ''}
                      </span>
                    </div>

                    <div className="text-sm text-gray-600 space-y-1">
                      {req.gallery_city && (
                        <div>📍 {req.gallery_city}, {req.gallery_country}</div>
                      )}
                      {req.gallery_website && (
                        <div className="flex items-center gap-2">
                          <a
                            href={req.gallery_website}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:underline inline-flex items-center gap-1"
                          >
                            {req.gallery_website}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => approveRequest(req.gallery_name)}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Approuver
                    </button>
                    
                    <button
                      onClick={() => setShowRejectModal(req.gallery_name)}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap"
                    >
                      <XCircle className="w-4 h-4" />
                      Rejeter
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Section 2: Galeries approuvées à ajouter */}
      {approvedRequests.length > 0 && (
        <div className="border-t-2 border-gray-200 pt-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-light text-gray-900">
              Approuvées - À ajouter ({approvedRequests.length})
            </h3>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-green-900">
              ✅ Ces galeries ont été approuvées. Clique sur "Ajouter la galerie" pour créer l'entrée dans la base de données.
            </p>
          </div>

          <div className="space-y-4">
            {approvedRequests.map((req) => (
              <div 
                key={req.gallery_name}
                className="bg-white border-2 border-green-200 rounded-lg p-6"
              >
                <div className="flex items-start justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-medium text-gray-900">
                        {req.gallery_name}
                      </h4>
                      
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded font-medium">
                        ✓ Approuvée
                      </span>
                    </div>

                    <div className="text-sm text-gray-600 space-y-1">
                      {req.gallery_city && (
                        <div>📍 {req.gallery_city}, {req.gallery_country}</div>
                      )}
                      {req.gallery_website && (
                        <div className="flex items-center gap-2">
                          <a
                            href={req.gallery_website}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:underline inline-flex items-center gap-1"
                          >
                            {req.gallery_website}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                      <div className="text-xs text-gray-400 mt-2">
                        Approuvée le {new Date(req.reviewed_at).toLocaleDateString("fr-FR")}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => setShowAddGalleryModal(req)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                  >
                    <Plus className="w-4 h-4" />
                    Ajouter la galerie
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal de rejet */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Rejeter la demande
              </h3>
              <button
                onClick={() => {
                  setShowRejectModal(null);
                  setRejectReason("");
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Galerie : <span className="font-medium">{showRejectModal}</span>
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Raison du rejet
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="Ex: Galerie ne correspond pas à nos critères"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(null);
                  setRejectReason("");
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={rejectRequest}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                disabled={!rejectReason.trim()}
              >
                Confirmer le rejet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal ajout galerie */}
      {showAddGalleryModal && (
        <AddGalleryForm
          galleryRequest={showAddGalleryModal}
          onClose={() => setShowAddGalleryModal(null)}
          onSuccess={() => {
            setShowAddGalleryModal(null);
            loadRequests();
          }}
        />
      )}
    </div>
  );
};

export default GalleryRequestsQueue;